import { useCallback, useEffect, useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import Lenis from "lenis";
import "./App.css";
import { CONFIG } from "./config";
import Crest from "./components/Crest";
import IntroCrest from "./components/IntroCrest";

const EASE = [0.22, 1, 0.36, 1];

function MaskReveal({ children, delay = 0, ready = true }) {
  return (
    <span className="mask">
      <motion.span
        className="mask-inner"
        initial={{ y: "115%" }}
        animate={ready ? { y: "0%" } : { y: "115%" }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function MaskInView({ children, delay = 0 }) {
  return (
    <motion.span
      className="mask"
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-40px" }}
    >
      <motion.span
        className="mask-inner"
        variants={{ hidden: { y: "115%" }, show: { y: "0%" } }}
        transition={{ duration: 0.9, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </motion.span>
  );
}

function Reveal({ children, delay = 0, className }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

function useLiveStatus(onLive) {
  const [status, setStatus] = useState({
    ...CONFIG.schedule,
    liveOn: [],
    streamWindow: CONFIG.streamWindow,
    nextStart: null,
    upcoming: [],
    inWindow: false,
    windowWho: null,
  });
  const baseline = useRef(false); // first successful fetch sets the baseline, never chimes
  const prevLive = useRef(false);
  useEffect(() => {
    if (!CONFIG.statusEndpoint) return undefined;
    let cancelled = false;
    const check = () => {
      fetch(CONFIG.statusEndpoint)
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then((data) => {
          if (cancelled) return;
          const nowLive = !!data.live;
          if (baseline.current && nowLive && !prevLive.current && onLive) onLive();
          baseline.current = true;
          prevLive.current = nowLive;
          setStatus({
            live: nowLive,
            liveOn: Array.isArray(data.liveOn) ? data.liveOn : [],
            nextDay: data.nextDay || CONFIG.schedule.nextDay,
            nextTime: data.nextTime || CONFIG.schedule.nextTime,
            streamWindow: data.streamWindow || CONFIG.streamWindow,
            nextStart: data.nextStart || null,
            upcoming: Array.isArray(data.upcoming) ? data.upcoming : [],
            inWindow: !!data.inWindow,
            windowWho: data.windowWho || null,
          });
        })
        .catch(() => {}); // silently keep the current state
    };
    check();
    const id = setInterval(check, 60000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [onLive]);
  return status;
}

function StatusChip({ status, ready = true }) {
  const online = !status.live && status.inWindow;
  return (
    <motion.div
      className={online ? "status-chip online" : "status-chip"}
      data-testid="status-chip"
      initial={{ opacity: 0, y: -10 }}
      animate={ready ? { opacity: 1, y: 0 } : { opacity: 0, y: -10 }}
      transition={{ duration: 0.8, delay: ready ? 0.15 : 0, ease: EASE }}
    >
      {status.live ? (
        <>
          <span className="live-dot" aria-hidden="true" />
          <span>
            Live now
            {status.liveOn.length > 0 ? ` — ${status.liveOn.join(" · ")}` : ""}
          </span>
        </>
      ) : online ? (
        <>
          <span className="online-dot" aria-hidden="true" />
          <span data-testid="online-label">
            Online{status.windowWho ? ` — ${status.windowWho}` : ""}
          </span>
        </>
      ) : (
        <>
          <span className="offline-dot" aria-hidden="true" />
          <span>
            Offline — next stream {status.nextDay} {status.nextTime}
          </span>
        </>
      )}
    </motion.div>
  );
}

function useStreamMs(nextStart) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!nextStart) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [nextStart]);
  return nextStart ? new Date(nextStart).getTime() - now : null;
}

function Countdown({ status, ready, ms }) {
  // While a watch is on, the chip already says Online — no countdown.
  if (status.live || status.inWindow || ms === null || ms <= 0) return null;
  const mins = Math.floor(ms / 60000);
  const imminent = ms <= 60000;
  const text =
    mins >= 1440
      ? `${Math.floor(mins / 1440)}d ${Math.floor((mins % 1440) / 60)}h`
      : mins >= 60
        ? `${Math.floor(mins / 60)}h ${mins % 60}m`
        : `${Math.max(mins, 1)}m`;
  // When today's first block is still ahead, the second one is too —
  // point at the later watch (Callia mornings, Lumira evenings).
  const second =
    status.upcoming.length >= 2 && status.upcoming[0].day === "Today"
      ? status.upcoming[1]
      : null;
  const secondWhen = second && second.day !== "Today" ? `${second.day} ` : "";
  return (
    <>
      <p className="countdown-line" data-testid="countdown-line">
        <MaskReveal delay={1.32} ready={ready}>
          {imminent ? (
            <span className="countdown-live" data-testid="going-live">
              <span className="live-dot" aria-hidden="true" />
              Going live
            </span>
          ) : (
            `Next stream in ${text}`
          )}
        </MaskReveal>
      </p>
      {second && !imminent && (
        <p className="second-block" data-testid="second-block-line">
          <MaskReveal delay={1.4} ready={ready}>
            Then again — {secondWhen}{second.label}
          </MaskReveal>
        </p>
      )}
    </>
  );
}

const SOON_TRACKS = [
  { title: "No Off Switch", src: "/no-off-switch.mp3" },
  { title: "Channel My Inner Beast", src: "/channel-my-inner-beast.mp3" },
  { title: "Channel My Inner Champion", src: "/channel-my-inner-champion.mp3" },
  { title: "LUMIRA — Lights Up", src: "/lumira-lights-up.mp3" },
  { title: "Leave It Lit", src: "/leave-it-lit.mp3" },
  { title: "PKing", src: "/geskano-pking.mp3" },
];

function SoonTrack() {
  // A hidden playlist, not a player: it autoplays from No Off Switch and
  // rolls through the tracks forever, wrapping back to the start. Browsers that
  // refuse audible autoplay get it silently until the first tap/keypress,
  // which unmutes + starts it. The button is only a pause/resume override.
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [trackIdx, setTrackIdx] = useState(0);
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return undefined;
    const sync = () => setPlaying(!a.paused);
    const onEnded = () => setTrackIdx((i) => (i + 1) % SOON_TRACKS.length);
    a.addEventListener("play", sync);
    a.addEventListener("pause", sync);
    a.addEventListener("ended", onEnded);
    a.volume = 0.9;
    const attempt = () =>
      a.play().catch(() => {
        a.muted = true;
        a.play().catch(() => {});
      });
    attempt();
    a.addEventListener("canplay", attempt); // retry once enough data is in
    const wake = () => {
      a.muted = false;
      if (a.paused) a.play().catch(() => {});
    };
    window.addEventListener("pointerdown", wake, { once: true });
    window.addEventListener("keydown", wake, { once: true });
    return () => {
      a.removeEventListener("play", sync);
      a.removeEventListener("pause", sync);
      a.removeEventListener("ended", onEnded);
      a.removeEventListener("canplay", attempt);
    };
  }, []);
  useEffect(() => {
    const a = audioRef.current;
    const t = SOON_TRACKS[trackIdx];
    if (!a || !t || a.src.endsWith(t.src)) return undefined;
    a.src = t.src;
    if (playing) a.play().catch(() => {});
    return undefined;
  }, [trackIdx, playing]);
  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
    } else {
      a.muted = false;
      a.play().catch(() => {});
    }
  };
  const current = SOON_TRACKS[trackIdx];
  return (
    <div className="soon-track" data-testid="soon-track">
      <audio ref={audioRef} src={SOON_TRACKS[0].src} preload="auto" />
      <button
        type="button"
        className={`soon-play${playing ? " playing" : ""}`}
        onClick={toggle}
        aria-label={playing ? "Pause the music" : "Play the music"}
        data-testid="soon-play-btn"
      >
        {playing ? (
          <span className="soon-eq" aria-hidden="true"><i /><i /><i /></span>
        ) : (
          <span className="soon-tri" aria-hidden="true" />
        )}
      </button>
      <span className="soon-track-label" data-testid="soon-track-label">
        {playing ? current.title : "Music for the wait"}
      </span>
    </div>
  );
}

function ComingSoon({ onLaunch, notifyFor = null }) {
  // Launch gate: counts down to the fixed launch moment (CONFIG.launchAt —
  // Friday, October 16, 2026, 8 PM ET). When the timer finishes, the site
  // opens for everyone.
  const ms = useStreamMs(CONFIG.launchAt);
  useEffect(() => {
    if (ms !== null && ms <= 0) onLaunch();
  }, [ms, onLaunch]);

  const total = Math.max(0, Math.floor((ms || 0) / 1000));
  const cells = [
    ["Days", Math.floor(total / 86400)],
    ["Hrs", Math.floor((total % 86400) / 3600)],
    ["Min", Math.floor((total % 3600) / 60)],
    ["Sec", total % 60],
  ];
  const when = new Date(CONFIG.launchAt).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone: "America/New_York",
  });
  return (
    <main className="soon" data-testid="coming-soon">
      <section className="hero">
        <div className="hero-glow" aria-hidden="true" />
        <div className="hero-inner soon-inner">
          <Crest size={120} />
          <h1 className="wordmark">Geskano</h1>
          <hr className="gold-rule" />
          <p className="soon-title">Coming soon</p>
          <div className="soon-timer" data-testid="soon-timer">
            {cells.map(([label, v]) => (
              <div className="soon-cell" key={label} data-testid={`soon-${label.toLowerCase()}`}>
                <b key={v}>{String(v).padStart(2, "0")}</b>
                <span>{label}</span>
              </div>
            ))}
          </div>
          <p className="soon-when" data-testid="soon-when">
            First stream — {when} · 8 PM ET
          </p>
          <SoonTrack />
        </div>
      </section>
      <EmailSection eyebrow="The one message" preselect={notifyFor} />
    </main>
  );
}

function Hero({ status, ready = true, morph = false, onImminent }) {
  const reduce = useReducedMotion();
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const rotateX = useTransform(sy, [0, 1], [7, -7]);
  const rotateY = useTransform(sx, [0, 1], [-7, 7]);
  const crestY = useTransform(sy, [0, 1], [10, -10]);

  const onMove = (e) => {
    if (reduce) return;
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width);
    my.set((e.clientY - rect.top) / rect.height);
  };

  const all = Object.values(CONFIG.platforms).flat();
  const buttons = CONFIG.heroPlatforms
    .map((name) => all.find((p) => p.name === name))
    .filter(Boolean);

  const ms = useStreamMs(status.nextStart);
  const imminent = !status.live && ms !== null && ms > 0 && ms <= 60000;

  // Chime when the countdown flips into the final minute while the visitor
  // is on the page — never on arrival (first reading sets the baseline).
  const imminentSeen = useRef(false);
  const prevImminent = useRef(false);
  useEffect(() => {
    if (ms === null) return;
    if (!imminentSeen.current) {
      imminentSeen.current = true;
      prevImminent.current = imminent;
      return;
    }
    if (imminent && !prevImminent.current && onImminent) onImminent();
    prevImminent.current = imminent;
  }, [imminent, ms, onImminent]);

  return (
    <header className={imminent ? "hero imminent" : "hero"} onMouseMove={onMove}>
      <div className="hero-glow" aria-hidden="true" />
      <div className="hero-inner">
        <StatusChip status={status} ready={ready} />
        <motion.div
          className="crest-wrap"
          initial={morph ? false : { opacity: 0, scale: 0.8, rotate: -8 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1.3, delay: morph ? 0 : 0.3, ease: EASE }}
          style={reduce ? undefined : { rotateX, rotateY, y: crestY }}
        >
          <Crest size={150} />
        </motion.div>
        <h1 className="wordmark" data-testid="wordmark">
          <MaskReveal delay={0.55} ready={ready}>Geskano</MaskReveal>
        </h1>
        <motion.hr
          className="gold-rule"
          initial={{ scaleX: 0 }}
          animate={ready ? { scaleX: 1 } : { scaleX: 0 }}
          transition={{ duration: 1.1, delay: ready ? 1 : 0, ease: EASE }}
        />
        <p className="tagline">
          <MaskReveal delay={1.15} ready={ready}>
            Gaming, and whatever the day turns into.
          </MaskReveal>
        </p>
        <p className="schedule-line" data-testid="schedule-line">
          <MaskReveal delay={1.25} ready={ready}>
            {status.streamWindow}
          </MaskReveal>
        </p>
        <Countdown status={status} ready={ready} ms={ms} />
        <nav className="hero-buttons" aria-label="Watch live">
          {buttons.map((p, i) => (
            <MaskReveal key={p.name} delay={1.42 + i * 0.09} ready={ready}>
              <a
                data-testid={`hero-btn-${p.name.toLowerCase()}`}
                className={i === 0 ? "btn btn-primary" : "btn btn-secondary"}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {p.name}
              </a>
            </MaskReveal>
          ))}
        </nav>
      </div>
    </header>
  );
}

function Marquee() {
  // One loop unit is deliberately longer than any viewport, so no phrase
  // ever appears twice on screen at once. The track is duplicated for a
  // seamless wrap.
  const row = [
    "Geskano",
    "Gaming",
    "Variety",
    "IRL",
    "Monday to Saturday",
    "Nights and mornings Eastern",
    "The games change",
    "The room doesn't",
    "Going in blind",
    "Losses stay in the VOD",
  ];
  const track = (hidden) => (
    <div className="marquee-track" aria-hidden={hidden || undefined}>
      {row.map((w, i) => (
        <span className="marquee-item" key={i}>
          {w}
          <span className="marquee-diamond" />
        </span>
      ))}
    </div>
  );
  return (
    <div className="marquee" data-testid="marquee">
      {track(false)}
      {track(true)}
    </div>
  );
}

const LANES = [
  {
    title: "Ranked",
    body: "Competitive runs with the climb actually explained. Losses stay in the VOD.",
  },
  {
    title: "First plays",
    body: "Going in blind on something new, with chat calling the shots more than they should.",
  },
  {
    title: "IRL",
    body: "Whatever's happening outside. Unscripted, occasionally a mistake.",
  },
];

function Channel() {
  return (
    <section className="section" id="channel" data-testid="channel-section">
      <Reveal>
        <p className="eyebrow">01 — The channel</p>
      </Reveal>
      <h2 className="heading">
        <MaskInView delay={0.1}>Two gamers, three lanes.</MaskInView>
      </h2>
      <div className="lane-grid">
        {LANES.map((lane, i) => (
          <Reveal key={lane.title} delay={0.15 + i * 0.12}>
            <motion.article
              className="lane-card"
              data-testid={`lane-${lane.title.toLowerCase().replace(/\s/g, "-")}`}
              whileHover={{ y: -5 }}
              transition={{ duration: 0.3, ease: EASE }}
            >
              <h3 className="lane-title">{lane.title}</h3>
              <p className="lane-body">{lane.body}</p>
            </motion.article>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <p className="channel-close">The games change; the room doesn't.</p>
      </Reveal>
      <Reveal delay={0.3}>
        <div className="roster">
          <p className="group-label">The roster</p>
          <div className="roster-cards">
            <motion.a
              className="gamer-card"
              href="/lumira"
              data-testid="gamer-lumira"
              aria-label="Meet Lumira, the Lamplighter"
              whileHover={{ y: -4 }}
              transition={{ duration: 0.3, ease: EASE }}
            >
              <span className="gamer-frame">
                <img src="/lumira.jpeg" alt="Lumira, the Lamplighter, holding a brass lantern" />
              </span>
              <span className="gamer-meta">
                <span className="gamer-name">Lumira</span>
                <span className="gamer-title">
                  The Lamplighter · 8 PM–5 AM ET · Sat till 12 AM
                </span>
                <span className="gamer-cta">Meet her</span>
              </span>
            </motion.a>
            <motion.a
              className="gamer-card"
              href="/callia"
              data-testid="gamer-callia"
              aria-label="Meet Callia, the Magpie"
              whileHover={{ y: -4 }}
              transition={{ duration: 0.3, ease: EASE }}
            >
              <span className="gamer-frame">
                <img src="/callia.jpeg" alt="Callia, the Magpie, with a magpie on her shoulder and a box of mended glass" />
              </span>
              <span className="gamer-meta">
                <span className="gamer-name">Callia</span>
                <span className="gamer-title">
                  The Magpie · 5 AM–2 PM ET · Sat till 9 AM
                </span>
                <span className="gamer-cta">Meet her</span>
              </span>
            </motion.a>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.4}>
        <div className="week" data-testid="week-schedule">
          <p className="group-label">The week · all times Eastern</p>
          <div className="week-rows">
            <div className="week-row" data-testid="week-weekday">
              <span className="week-day">Mon–Fri</span>
              <span className="week-who">Lumira <i>8 PM–5 AM</i></span>
              <span className="week-who">Callia <i>5 AM–2 PM</i></span>
            </div>
            <div className="week-row" data-testid="week-saturday">
              <span className="week-day">Saturday</span>
              <span className="week-who">Lumira <i>8 PM–12 AM</i></span>
              <span className="week-who">Callia <i>5–9 AM</i></span>
            </div>
            <div className="week-row" data-testid="week-sunday">
              <span className="week-day">Sunday</span>
              <span className="week-dark">The room is dark</span>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

function Watch({ status }) {
  return (
    <section className="section" id="watch" data-testid="watch-section">
      <Reveal>
        <p className="eyebrow">02 — Watch</p>
      </Reveal>
      <h2 className="heading">
        <MaskInView delay={0.1}>Everywhere else.</MaskInView>
      </h2>
      {Object.entries(CONFIG.platforms).map(([group, links]) => (
        <div className="platform-group" key={group}>
          <Reveal>
            <h3 className="group-label">{group}</h3>
          </Reveal>
          <div className="platform-grid">
            {links.map((p, i) => {
              const isLive = group === "Live" && status.liveOn.includes(p.name);
              return (
                <Reveal key={p.name} delay={0.05 * i}>
                  <motion.a
                    data-testid={`platform-${p.name.toLowerCase()}`}
                    className={
                      isLive ? "platform-card platform-card-live" : "platform-card"
                    }
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={
                      isLive
                        ? `Geskano on ${p.name} — live now`
                        : `Geskano on ${p.name}`
                    }
                    whileHover={{ y: -4 }}
                    transition={{ duration: 0.3, ease: EASE }}
                  >
                    <span className="platform-name">{p.name}</span>
                    <span className="platform-handle">{p.handle}</span>
                    {isLive && (
                      <span
                        className="live-badge"
                        data-testid={`live-badge-${p.name.toLowerCase()}`}
                      >
                        <span className="live-dot" aria-hidden="true" />
                        Live
                      </span>
                    )}
                  </motion.a>
                </Reveal>
              );
            })}
          </div>
        </div>
      ))}
    </section>
  );
}

function EmailSection({ eyebrow = "03 — Email", preselect = null }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | success | error
  const [choice, setChoice] = useState("all"); // "all" or a performer slug
  const [roster, setRoster] = useState([]);

  // The pills come from the live registry — a new performer appears here
  // the moment their entry exists. No registry reachable, no pills.
  useEffect(() => {
    if (!CONFIG.rosterEndpoint) return undefined;
    let dead = false;
    fetch(CONFIG.rosterEndpoint)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        if (!dead) setRoster((d.performers || []).filter((p) => p.slug !== "geskano"));
      })
      .catch(() => {});
    return () => {
      dead = true;
    };
  }, []);

  // Deep-linked from a performer's page (/?for=<slug>#notify): preselect her.
  useEffect(() => {
    if (preselect && roster.some((p) => p.slug === preselect)) setChoice(preselect);
  }, [preselect, roster]);

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    try {
      const resp = await fetch(CONFIG.emailEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          performers: choice === "all" ? [] : [choice],
        }),
      });
      if (!resp.ok) throw new Error();
      setState("success");
    } catch {
      setState("error");
    }
  };

  return (
    <section className="section" id="notify" data-testid="email-section">
      <Reveal>
        <p className="eyebrow">{eyebrow}</p>
      </Reveal>
      <h2 className="heading">
        <MaskInView delay={0.1}>Get told when it's on.</MaskInView>
      </h2>
      <Reveal delay={0.15}>
        <p className="email-body">
          One message when a stream goes live and nothing else. No newsletter, no
          drip sequence, no merch launches you didn't ask about.
        </p>
      </Reveal>
      <Reveal delay={0.25}>
        {state === "success" ? (
          <p className="form-note note-success" data-testid="email-success" role="status">
            You're on the list. See you at the next one.
          </p>
        ) : (
          <>
            {roster.length > 0 && (
              <div className="notify-choice" data-testid="notify-choice">
                <p className="notify-choice-label">Tell me about</p>
                <button
                  type="button"
                  className={choice === "all" ? "on" : ""}
                  onClick={() => setChoice("all")}
                  data-testid="notify-choice-all"
                >
                  Everyone
                </button>
                {roster.map((p) => (
                  <button
                    key={p.slug}
                    type="button"
                    className={choice === p.slug ? "on" : ""}
                    onClick={() => setChoice(p.slug)}
                    data-testid={`notify-choice-${p.slug}`}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            )}
            <form className="email-form" onSubmit={submit} data-testid="email-form">
              <label htmlFor="notify-email" className="sr-only">
                Email address
              </label>
              <input
                id="notify-email"
                data-testid="email-input"
                type="email"
                required
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (state === "error") setState("idle");
                }}
              />
              <button
                type="submit"
                className="btn btn-primary"
                data-testid="email-submit"
                disabled={state === "sending"}
              >
                {state === "sending" ? "One moment" : "Notify me"}
              </button>
            </form>
          </>
        )}
        {state === "error" && (
          <p className="form-note note-error" data-testid="email-error" role="alert">
            That didn't go through. Try again in a moment.
          </p>
        )}
        <p className="fine-print">Unsubscribe any time · No list sharing</p>
      </Reveal>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer" data-testid="footer">
      <Crest size={22} />
      <span className="footer-wordmark">Geskano</span>
      <nav className="footer-links" aria-label="Footer">
        <a href="#channel">Channel</a>
        <a href="#watch">Watch</a>
        <a href="#notify">Notify</a>
        <a href="/lumira" data-testid="footer-lumira">Lumira</a>
        <a href="/callia" data-testid="footer-callia">Callia</a>
      </nav>
      <span className="footer-year">{new Date().getFullYear()}</span>
    </footer>
  );
}

function playSwell() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  const ctx = new Ctx();
  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.setValueAtTime(0, now);
  master.gain.linearRampToValueAtTime(0.14, now + 1.1);
  master.gain.linearRampToValueAtTime(0.0001, now + 3.8);
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(320, now);
  filter.frequency.linearRampToValueAtTime(1300, now + 1.5);
  filter.frequency.linearRampToValueAtTime(300, now + 3.8);
  master.connect(filter).connect(ctx.destination);
  [110, 164.81, 220, 277.18].forEach((f, i) => {
    const osc = ctx.createOscillator();
    osc.type = i < 2 ? "sine" : "triangle";
    osc.frequency.value = f * (1 + (i % 2 ? 0.0015 : -0.0015));
    const g = ctx.createGain();
    g.gain.value = i < 2 ? 0.5 : 0.18;
    osc.connect(g).connect(master);
    osc.start(now);
    osc.stop(now + 4.0);
  });
  return ctx;
}

// If the browser has suspended audio (no interaction yet), start the sound on
// the visitor's first scroll, tap, or keypress — the scheduled notes wait.
function resumeOnGesture(ctx) {
  const events = ["pointerdown", "keydown", "wheel", "touchstart"];
  const resume = () => {
    ctx.resume();
    events.forEach((e) => window.removeEventListener(e, resume));
  };
  events.forEach((e) => window.addEventListener(e, resume));
}

// A single soft two-note bell (E5, then A5) with a faint octave shimmer.
function playChime() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  const ctx = new Ctx();
  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.setValueAtTime(0.12, now);
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 3200;
  master.connect(filter).connect(ctx.destination);
  [
    [659.25, 0],
    [880, 0.35],
  ].forEach(([f, offset]) => {
    const t = now + offset;
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = f;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(1, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.8);
    const shimmer = ctx.createOscillator();
    shimmer.type = "sine";
    shimmer.frequency.value = f * 2;
    const gs = ctx.createGain();
    gs.gain.setValueAtTime(0, t);
    gs.gain.linearRampToValueAtTime(0.25, t + 0.012);
    gs.gain.exponentialRampToValueAtTime(0.0001, t + 1.1);
    osc.connect(g).connect(master);
    shimmer.connect(gs).connect(master);
    osc.start(t);
    osc.stop(t + 2);
    shimmer.start(t);
    shimmer.stop(t + 1.3);
  });
  return ctx;
}

function Intro({ onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3400);
    return () => clearTimeout(t);
  }, [onDone]);

  // Automatic swell: scheduled the moment the intro starts.
  useEffect(() => {
    const ctx = playSwell();
    if (ctx && ctx.state === "suspended") resumeOnGesture(ctx);
  }, []);

  return (
    <motion.div
      className="intro"
      data-testid="intro"
      exit={{ opacity: 0, transition: { duration: 0.7, ease: "easeInOut" } }}
    >
      <div className="hero-glow" aria-hidden="true" />
      <motion.div
        className="intro-scan"
        aria-hidden="true"
        initial={{ top: "-2%", opacity: 0 }}
        animate={{ top: "102%", opacity: [0, 1, 1, 0] }}
        transition={{ duration: 1.15, delay: 0.1, ease: "linear", times: [0, 0.08, 0.9, 1] }}
      />
      <motion.div
        className="intro-ring intro-ring-a"
        aria-hidden="true"
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.5 }}
        transition={{ duration: 1.6, delay: 0.45, ease: EASE }}
      />
      <motion.div
        className="intro-ring intro-ring-b"
        aria-hidden="true"
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.3 }}
        transition={{ duration: 1.9, delay: 0.62, ease: EASE }}
      />
      <motion.span
        className="intro-pulse"
        aria-hidden="true"
        initial={{ scale: 0.3, opacity: 0.7 }}
        animate={{ scale: 2, opacity: 0 }}
        transition={{ duration: 0.9, delay: 2.15, ease: EASE }}
      />
      <motion.div
        className="intro-crest"
        exit={{ scale: 0.75, opacity: 0, transition: { duration: 0.35, ease: EASE } }}
      >
        <IntroCrest size={200} />
      </motion.div>
      <motion.p className="intro-word" aria-label="Geskano">
        {"GESKANO".split("").map((ch, i) => (
          <motion.span
            key={i}
            style={{ display: "inline-block" }}
            initial={{ opacity: 0, y: 10, filter: "blur(5px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.5, delay: 2.0 + i * 0.06, ease: EASE }}
          >
            {ch}
          </motion.span>
        ))}
      </motion.p>
      <motion.div
        className="intro-rule"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.7, delay: 2.6, ease: EASE }}
      />
    </motion.div>
  );
}

function BlockedScreen() {
  return (
    <main className="blocked" data-testid="blocked-screen">
      <Crest size={72} />
      <p className="blocked-title">This page can&rsquo;t be reached</p>
      <p className="blocked-body">
        The connection was interrupted. Check your network and try again.
      </p>
    </main>
  );
}

function MaintenanceScreen() {
  // The closed sign — flips on from The Ledger, covers every public page.
  return (
    <main className="blocked" data-testid="maintenance-screen">
      <Crest size={72} />
      <p className="blocked-title">Under maintenance</p>
      <p className="blocked-body">
        This website is under maintenance &mdash; be back soon.
      </p>
    </main>
  );
}

function PerformerPage({ slug }) {
  // Registry-driven performer page (Phase 2, network of pages): everything
  // on it — name, title, bio, accent, watch hours, links — comes from the
  // performer's registry entry. Lumira and Callia keep their hand-built
  // pages; this scaffold is how FUTURE performers get theirs.
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | missing
  useEffect(() => {
    let dead = false;
    fetch(`${CONFIG.rosterEndpoint}/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        if (dead) return;
        setData(d);
        setState("ready");
      })
      .catch(() => !dead && setState("missing"));
    return () => {
      dead = true;
    };
  }, [slug]);
  const ms = useStreamMs(data?.status?.nextStart || null);

  if (state === "loading") {
    return (
      <main className="perf" data-testid="performer-loading">
        <Crest size={72} />
      </main>
    );
  }
  if (state === "missing") {
    return (
      <main className="perf" data-testid="performer-missing">
        <div className="perf-crest">
          <Crest size={72} />
        </div>
        <h1 className="perf-name">No one by that name</h1>
        <hr className="perf-rule" />
        <div className="perf-links">
          <a href="/" data-testid="performer-home-link">Geskano — home</a>
        </div>
      </main>
    );
  }

  const p = data.performer;
  const s = data.status || {};
  const accent = p.accent || undefined;
  let countLine = null;
  if (s.inWindow) {
    countLine = "On now — the watch is lit";
  } else if (ms !== null && ms > 0) {
    const mins = Math.floor(ms / 60000);
    const rel =
      mins >= 1440
        ? `${Math.floor(mins / 1440)}d ${Math.floor((mins % 1440) / 60)}h`
        : mins >= 60
          ? `${Math.floor(mins / 60)}h ${mins % 60}m`
          : `${Math.max(mins, 1)}m`;
    countLine = `${s.day} · ${s.label} · in ${rel}`;
  }

  return (
    <main
      className="perf"
      style={accent ? { "--paccent": accent } : undefined}
      data-testid="performer-page"
    >
      <div className="perf-crest">
        <Crest size={72} />
      </div>
      <p className="perf-eyebrow">The roster · Geskano</p>
      <h1 className="perf-name" data-testid="performer-name">{p.name}</h1>
      {p.title && <p className="perf-title" data-testid="performer-title">{p.title}</p>}
      <hr className="perf-rule" />
      {p.bio && <p className="perf-bio" data-testid="performer-bio">{p.bio}</p>}
      {s.summary && (
        <p className="perf-watch" data-testid="performer-schedule">
          The watch — {s.summary}
        </p>
      )}
      {countLine && (
        <p className={`perf-count${s.inWindow ? " on" : ""}`} data-testid="performer-countdown">
          <span className="dot" aria-hidden="true" />
          {countLine}
        </p>
      )}
      <div className="perf-links" data-testid="performer-links">
        {(p.links || []).map((l) => (
          <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer">
            {l.name}
          </a>
        ))}
        <a href="/" data-testid="performer-home-link">Geskano — home</a>
      </div>
      <EmailSection eyebrow="The one message" preselect={slug} />
    </main>
  );
}

export default function App() {
  // Chime when the channel goes live, or when the countdown flips to
  // "Going live", while someone is on the page.
  const chime = useCallback(() => {
    const ctx = playChime();
    if (ctx && ctx.state === "suspended") resumeOnGesture(ctx);
  }, []);
  const status = useLiveStatus(chime);
  const reduce = useReducedMotion();
  const [blocked, setBlocked] = useState(false);
  const [maintenance, setMaintenance] = useState(false);
  // /?for=<slug>#notify — a performer page sent them here; preselect her pill.
  const notifyFor = new URLSearchParams(window.location.search).get("for");
  const perfMatch = window.location.pathname.match(/^\/p\/([a-z0-9-]{2,30})\/?$/);

  // Access gate: blocks detected VPN/proxy/Tor visitors and logs the visit.
  // Fails open — if the check can't run, everyone gets in. The same call
  // carries the maintenance flag — the closed sign covers everything public.
  useEffect(() => {
    if (!CONFIG.gateEndpoint) return;
    fetch(`${CONFIG.gateEndpoint}?path=${encodeURIComponent(window.location.pathname)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        if (d.maintenance) setMaintenance(true);
        if (d.blocked) setBlocked(true);
      })
      .catch(() => {});
  }, []);
  // Coming-soon gate: the site stays hidden until the launch moment
  // (CONFIG.launchAt — Friday, October 16, 2026, 8 PM ET). Past that moment
  // the gate never shows; a visitor mid-countdown gets the reveal live.
  // Owner preview: ?site=open remembers "opened" for this browser tab only;
  // ?site=soon puts the curtain back. No UI hints — the URL is the key.
  const siteParams = new URLSearchParams(window.location.search);
  if (siteParams.get("site") === "open") sessionStorage.setItem("geskano-preview-open", "1");
  if (siteParams.get("site") === "soon") sessionStorage.removeItem("geskano-preview-open");
  const previewOpen = sessionStorage.getItem("geskano-preview-open") === "1";
  const [launched, setLaunched] = useState(
    () =>
      !CONFIG.comingSoon ||
      previewOpen ||
      Date.now() >= new Date(CONFIG.launchAt).getTime() ||
      (typeof window !== "undefined" &&
        localStorage.getItem("geskano-launched") === "1")
  );
  const [intro, setIntro] = useState(
    () =>
      launched &&
      typeof window !== "undefined" &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  // Whether the intro played this load — the hero crest then mounts already
  // assembled (the intro crest handed off to it) instead of re-entering.
  const introPlayed = useRef(intro).current;

  useEffect(() => {
    if (reduce || intro) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, [reduce, intro]);

  const finishIntro = () => {
    setIntro(false);
  };

  // Reveal: persist so the gate never comes back for this visitor, and let
  // the intro sequence play as the grand opening.
  const handleLaunch = useCallback(() => {
    try {
      localStorage.setItem("geskano-launched", "1");
    } catch {}
    setLaunched(true);
    if (
      typeof window !== "undefined" &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setIntro(true);
    }
  }, []);

  // Deep links (e.g. /#notify from the character pages): the browser's own
  // anchor jump fires before React mounts, so redo it once the intro ends.
  useEffect(() => {
    if (intro) return undefined;
    const id = window.location.hash.slice(1);
    if (!id) return undefined;
    const t = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "auto", block: "start" });
    }, 100);
    return () => clearTimeout(t);
  }, [intro]);

  if (maintenance) return <MaintenanceScreen />;
  if (blocked) return <BlockedScreen />;
  // Registry-driven performer pages stay public behind the coming-soon
  // gate, same as the hand-built /lumira and /callia.
  if (perfMatch) return <PerformerPage slug={perfMatch[1]} />;
  if (!launched) return <ComingSoon status={status} onLaunch={handleLaunch} notifyFor={notifyFor} />;

  return (
    <>
      <AnimatePresence>{intro && <Intro onDone={finishIntro} />}</AnimatePresence>
      <main className="site">
        <Hero status={status} ready={!intro} morph={introPlayed} onImminent={chime} />
        <Marquee />
        <Channel />
        <Watch status={status} />
        <EmailSection preselect={notifyFor} />
        <Footer />
      </main>
    </>
  );
}
